/**
 * Column definitions for the admin Contacts blotter.
 *
 * ── The null convention ─────────────────────────────────────────────────────────────────
 * A row field the backend returns as null is not the same thing on screen and in the CSV, so
 * every nullable column declares both halves: `orDash` renders EMPTY_CELL ("—") in the table,
 * `orBlank` writes "" in the export. A spreadsheet cell holding an em dash is not empty — it
 * sorts and filters as text — which is why the CSV never reuses the screen placeholder.
 *
 * ── Why the cells take deps instead of calling hooks ────────────────────────────────────
 * `render`, `sortValue` and `exportValue` are plain renderers called per row by the Blotter, and
 * a renderer may not call hooks (Blotter's hooks-vs-renderers rule). Anything hook-derived — the
 * clipboard write behind the Redemption Link cell — is resolved by the panel and passed down
 * through `AdminContactsColumnDeps`, which is why `buildAllColumns` is a function and not a
 * module constant.
 */
import type { CSSProperties } from "react";
import RedemptionLinkCell from "@/components/admin/contacts/RedemptionLinkCell";
import type { BlotterColumn } from "@/components/Blotter";
import CampaignStatusChip from "@/components/campaigns/CampaignStatusChip";
import { COLUMN_LABELS, EMPTY_CELL } from "@/constants/adminContacts";
import type { AdminContactRow, ContactColumnId } from "@/interfaces/adminContacts";
import { applyPrefs, type ColumnPrefs } from "@/utils/columnPrefs-helper";
import { formatDate } from "@/utils/date-format";

/** Hook-derived values the cells close over, resolved by the panel (Blotter's hooks-vs-renderers rule). */
export interface AdminContactsColumnDeps {
  /** Asks the panel to copy `url` to the clipboard; the panel performs the write and shows the toast. */
  onCopy: (url: string) => void;
}

/**
 * Every column the API will sort by: `ContactColumnId` minus the one column declared
 * `sortable: false`. Same type as `AdminContactListParams["sortBy"]`; it lives here because this
 * is the file that decides which columns are sortable.
 */
export type SortableContactColumnId = Exclude<ContactColumnId, "redemption_link">;

export const SORTABLE_COLUMN_IDS: readonly SortableContactColumnId[] = [
  "name",
  "email",
  "organization",
  "campaign",
  "status",
  "account_lead",
  "client_name",
  "client_email",
  "delivered",
  "campaign_count",
];

/**
 * Base cell style. `cellStyle` **replaces** the Blotter's own `td` rather than merging with it
 * (`<td style={c.cellStyle ?? td}>`), so a column needing one extra property has to restate the rest.
 */
const TD: CSSProperties = {
  padding: "14px 16px",
  fontSize: 14,
  color: "var(--color-rodeo-black)",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

/** The redemption URL is the one cell long enough to need clamping for its ellipsis to trigger. */
const LINK_TD: CSSProperties = { ...TD, maxWidth: 280 };

const MUTED: CSSProperties = { color: "var(--color-graphite)" };
const PRIMARY: CSSProperties = { fontWeight: 600 };

/** Screen half of the null convention (see file doc above). */
function orDash(value: string | null): string {
  return value?.trim() ? value : EMPTY_CELL;
}

/** CSV half of the null convention (see file doc above). */
function orBlank(value: string | null): string {
  return value?.trim() ? value : "";
}

/**
 * Every column keyed by id, ready for `applyPrefs`.
 *
 * A function rather than a module constant because the Redemption Link cell needs the panel's
 * `onCopy` (see the file doc above for why that can't be a hook here).
 */
export function buildAllColumns(
  deps: AdminContactsColumnDeps,
): Record<ContactColumnId, BlotterColumn<AdminContactRow>> {
  return {
    name: {
      key: "name",
      label: COLUMN_LABELS.name,
      sortable: true,
      sortValue: (r) => r.name,
      exportValue: (r) => orBlank(r.name),
      render: (r) => <span style={PRIMARY}>{orDash(r.name)}</span>,
    },
    email: {
      key: "email",
      label: COLUMN_LABELS.email,
      sortable: true,
      sortValue: (r) => r.email,
      exportValue: (r) => orBlank(r.email),
      render: (r) => <span style={MUTED}>{orDash(r.email)}</span>,
    },
    organization: {
      key: "organization",
      label: COLUMN_LABELS.organization,
      sortable: true,
      sortValue: (r) => orBlank(r.organization),
      exportValue: (r) => orBlank(r.organization),
      render: (r) => orDash(r.organization),
    },
    campaign: {
      // Plain text, not a link: decision D4 — the Campaigns screen this would navigate to does not
      // exist yet, so the ticket's "Campaign cell navigates…" is deliberately unmet for now.
      key: "campaign",
      label: COLUMN_LABELS.campaign,
      sortable: true,
      sortValue: (r) => r.campaignName,
      exportValue: (r) => orBlank(r.campaignName),
      render: (r) => orDash(r.campaignName),
    },
    status: {
      key: "status",
      label: COLUMN_LABELS.status,
      sortable: true,
      sortValue: (r) => r.status,
      exportValue: (r) => r.status,
      render: (r) => <CampaignStatusChip status={r.status} />,
    },
    account_lead: {
      key: "account_lead",
      label: COLUMN_LABELS.account_lead,
      sortable: true,
      sortValue: (r) => orBlank(r.accountLead),
      exportValue: (r) => orBlank(r.accountLead),
      render: (r) => orDash(r.accountLead),
    },
    client_name: {
      key: "client_name",
      label: COLUMN_LABELS.client_name,
      sortable: true,
      sortValue: (r) => orBlank(r.clientName),
      exportValue: (r) => orBlank(r.clientName),
      render: (r) => orDash(r.clientName),
    },
    client_email: {
      key: "client_email",
      label: COLUMN_LABELS.client_email,
      sortable: true,
      sortValue: (r) => orBlank(r.clientEmail),
      exportValue: (r) => orBlank(r.clientEmail),
      render: (r) => <span style={MUTED}>{orDash(r.clientEmail)}</span>,
    },
    redemption_link: {
      // The only non-sortable column: `redemption_link` is absent from the backend's whitelist, so
      // clicking this header would earn a 400. The CSV keeps the full URL, protocol included —
      // the cell hides the protocol for width, a spreadsheet needs it to be clickable.
      key: "redemption_link",
      label: COLUMN_LABELS.redemption_link,
      sortable: false,
      exportValue: (r) => orBlank(r.redemptionLink),
      cellStyle: LINK_TD,
      render: (r) =>
        r.redemptionLink ? (
          <RedemptionLinkCell url={r.redemptionLink} onCopy={deps.onCopy} />
        ) : (
          EMPTY_CELL
        ),
    },
    delivered: {
      key: "delivered",
      label: COLUMN_LABELS.delivered,
      sortable: true,
      // Sorted by the raw ISO string, not the formatted date: "07/14/2026" sorts alphabetically.
      sortValue: (r) => orBlank(r.delivered),
      exportValue: (r) => (r.delivered ? formatDate(r.delivered) : ""),
      render: (r) => <span style={MUTED}>{formatDate(r.delivered)}</span>,
    },
    campaign_count: {
      key: "campaign_count",
      label: COLUMN_LABELS.campaign_count,
      sortable: true,
      sortValue: (r) => r.campaignCount,
      exportValue: (r) => String(r.campaignCount),
      render: (r) => r.campaignCount,
    },
  };
}

/**
 * The columns to hand the Blotter: Recipient first and always (R13), then the configurable columns
 * the user left visible, in the order they arranged them. Called on every render — the Blotter
 * renders the columns in the order it receives them and builds the CSV from the same list, which
 * is what makes "the CSV matches the visible columns, in order" (R16) fall out for free.
 */
export function buildColumns(
  prefs: ColumnPrefs<ContactColumnId>,
  deps: AdminContactsColumnDeps,
): BlotterColumn<AdminContactRow>[] {
  const all = buildAllColumns(deps);
  return [all.name, ...applyPrefs(all, prefs)];
}
